/* ─── Token storage ─────────────────────────────────────────────────────── */

const ACCESS_KEY = "nexora_access_token";
const REFRESH_KEY = "nexora_refresh_token";

function validToken(raw: string | null): string | null {
  // Guard against localStorage storing the literal strings "undefined" or "null"
  if (!raw || raw === "undefined" || raw === "null") return null;
  return raw;
}

export const tokenStore = {
  getAccess: () => validToken(localStorage.getItem(ACCESS_KEY)),
  getRefresh: () => validToken(localStorage.getItem(REFRESH_KEY)),
  setAccess: (t: string | undefined | null) => {
    if (!t || t === "undefined" || t === "null") return;
    localStorage.setItem(ACCESS_KEY, t);
  },
  setRefresh: (t: string | undefined | null) => {
    if (!t || t === "undefined" || t === "null") return;
    localStorage.setItem(REFRESH_KEY, t);
  },
  clear: () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

/* ─── Error message resolution ──────────────────────────────────────────── */

// Human-readable messages for backend error codes (from nexora API contract)
const CODE_MESSAGES: Record<string, string> = {
  INVALID_AUTH: "Invalid email or password. Please try again.",
  FORBIDDEN: "You do not have permission to perform this action.",
  VALIDATION_ERROR: "Some fields are invalid. Please check your input.",
  NOT_FOUND: "The requested record was not found.",
  RATE_LIMITED: "Too many requests. Please wait a moment and try again.",
  INTERNAL_ERROR: "An unexpected server error occurred. Please try again.",
};

// Fallback messages keyed by HTTP status when the backend sends no code/message
const STATUS_MESSAGES: Record<number, string> = {
  400: "Bad request — please check your input.",
  401: "Your session has expired. Please sign in again.",
  403: "You do not have permission to perform this action.",
  404: "The requested resource was not found.",
  409: "A record with these details already exists (e.g. email is already registered).",
  422: "The submitted data failed validation. Please review the form.",
  429: "Too many requests. Please slow down and try again shortly.",
  500: "Internal server error. Please try again later.",
  502: "Service is temporarily unreachable. Please try again.",
  503: "Service unavailable. Please try again later.",
};

function resolveErrorMessage(
  status: number,
  code?: string,
  serverMessage?: string,
): string {
  // 1. Prefer the backend's own human-readable message if it looks useful
  if (
    serverMessage &&
    serverMessage.length > 0 &&
    !serverMessage.startsWith("HTTP ")
  ) {
    return serverMessage;
  }
  // 2. Map backend error code to a friendly string
  if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];
  // 3. Fall back to HTTP status description
  if (STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];
  // 4. Last resort
  return `Request failed (HTTP ${status}). Please try again.`;
}

/* ─── Session-expired callback ──────────────────────────────────────────── */

let _onSessionExpired: (() => void) | null = null;
export function setSessionExpiredHandler(fn: () => void) {
  _onSessionExpired = fn;
}

/* ─── HTTP client ───────────────────────────────────────────────────────── */

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  retry = true,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...extraHeaders,
  };
  const token = tokenStore.getAccess();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error(
      "Unable to reach the server. Please check your network connection.",
    );
  }

  // Auto-refresh on 401
  if (res.status === 401 && retry) {
    const refreshed = await ensureFreshToken();
    if (refreshed) return request<T>(method, path, body, false, extraHeaders);
    tokenStore.clear();
    if (_onSessionExpired) _onSessionExpired();
    else window.location.href = "/login";
    throw new Error("Your session has expired. Please sign in again.");
  }

  if (!res.ok) {
    let errBody: {
      code?: string;
      message?: string;
      error?: string;
      detail?: string;
      title?: string;
    } = {};
    try {
      errBody = await res.json();
    } catch {
      /* body may not be JSON */
    }

    const message = resolveErrorMessage(
      res.status,
      errBody.code,
      errBody.message || errBody.error || errBody.detail || errBody.title,
    );

    const err = new Error(message) as ApiError;
    err.code = errBody.code;
    err.status = res.status;
    throw err;
  }

  // 204 No Content
  if (res.status === 204) return undefined as unknown as T;

  const json: unknown = await res.json();

  // Unwrap { success: true, data: ... } envelope that the backend uses
  if (
    json !== null &&
    typeof json === "object" &&
    "success" in json &&
    "data" in json
  ) {
    return (json as { data: T }).data;
  }

  return json as T;
}

async function uploadFile<T>(path: string, file: File): Promise<T> {
  const headers: Record<string, string> = {};
  const token = tokenStore.getAccess();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const formData = new FormData();
  formData.append("file", file);

  let res: Response;
  try {
    res = await fetch(path, { method: "POST", headers, body: formData });
  } catch {
    throw new Error(
      "Unable to reach the server. Please check your network connection.",
    );
  }

  if (!res.ok) {
    let errBody: { code?: string; message?: string } = {};
    try {
      errBody = await res.json();
    } catch {
      /* body may not be JSON */
    }
    const message = resolveErrorMessage(
      res.status,
      errBody.code,
      errBody.message,
    );
    const err = new Error(message) as ApiError;
    err.code = errBody.code;
    err.status = res.status;
    throw err;
  }

  const json: unknown = await res.json();
  if (
    json !== null &&
    typeof json === "object" &&
    "success" in json &&
    "data" in json
  ) {
    return (json as { data: T }).data;
  }
  return json as T;
}

// The backend rotates refresh tokens on every use (old one is revoked server-side,
// a new one issued). Two things follow from that:
//  1. We MUST persist the new refresh token here, not just the access token —
//     otherwise the next refresh attempt reuses the now-revoked old one and fails,
//     forcing a hard logout after exactly one silent refresh.
//  2. Concurrent 401s (several requests firing in parallel, e.g. on page load)
//     must share a single in-flight refresh call — if two requests each read the
//     same refresh token and POST it separately, only the first succeeds and the
//     second gets rejected as already-revoked, which also forces a logout even
//     though the session is actually fine.
let refreshInFlight: Promise<boolean> | null = null;

export function ensureFreshToken(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doRefresh(): Promise<boolean> {
  const token = tokenStore.getRefresh();
  if (!token) return false;
  try {
    const res = await fetch("/api/v1/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: token }),
    });
    if (!res.ok) return false;
    let raw: Record<string, string> = await res.json();
    // Unwrap { success, data } envelope if present
    if (raw && "success" in raw && "data" in raw) {
      raw = (raw as unknown as { data: Record<string, string> }).data;
    }
    // Accept camelCase or snake_case
    const newAccess = raw.accessToken || raw.access_token || raw.token;
    const newRefresh = raw.refreshToken || raw.refresh_token;
    if (!newAccess) return false;
    tokenStore.setAccess(newAccess);
    if (newRefresh) tokenStore.setRefresh(newRefresh);
    return true;
  } catch {
    return false;
  }
}

export interface ApiError extends Error {
  code?: string;
  status?: number;
}

const get = <T>(path: string) => request<T>("GET", path);
const post = <T>(path: string, body?: unknown) =>
  request<T>("POST", path, body);
const put = <T>(path: string, body?: unknown) => request<T>("PUT", path, body);
const patch = <T>(path: string, body?: unknown) =>
  request<T>("PATCH", path, body);
const del = <T>(path: string) => request<T>("DELETE", path);

// Endpoints annotated @Idempotent on the backend (e.g. payment order creation/
// verification) reject requests with no Idempotency-Key header. Each call site
// generates a fresh key per user-initiated action; retries within `request`'s
// own 401-refresh flow reuse the same key since it's the same logical attempt.
const postIdempotent = <T>(path: string, body?: unknown) =>
  request<T>("POST", path, body, true, {
    "Idempotency-Key": crypto.randomUUID(),
  });

// Spring Boot list endpoints return either a plain array or a Page<T> object.
async function getList<T>(path: string): Promise<T[]> {
  const res = await get<T[] | { content: T[] }>(path);
  return Array.isArray(res) ? res : (res?.content ?? []);
}

// Spring Data Page<T> shape, returned as `data` inside the {success, data} envelope.
export interface PageResult<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

async function getPage<T>(path: string): Promise<PageResult<T>> {
  return get<PageResult<T>>(path);
}

// Raw fetch for CSV/blob export endpoints (mirrors ledger.exportCsv / reportsApi.exportBlob).
async function downloadFile(path: string): Promise<Blob> {
  const token = tokenStore.getAccess();
  const res = await fetch(path, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error("Failed to export file");
  return res.blob();
}

/* ─── Types ─────────────────────────────────────────────────────────────── */

export interface Block {
  id: string;
  societyId: string;
  name: string;
  floorCount: number;
  createdAt: string;
}

export interface UnitType {
  id: string;
  societyId: string;
  name: string;
  createdAt: string;
}

export interface UnitOccupant {
  id: string;
  fullName: string;
  phone?: string;
  email?: string;
  type?: string;
}

export interface Unit {
  id: string;
  societyId: string;
  blockId?: string;
  unitTypeId?: string;
  unitNumber: string;
  floor?: number;
  areaSqft?: number;
  status: "occupied" | "vacant";
  createdAt: string;
  // joined fields the backend may include
  blockName?: string;
  unitTypeName?: string;
  occupants?: UnitOccupant[];
}

export type TeamMemberRole =
  | "STAFF"
  | "GUARD"
  | "TREASURER"
  | "VIEW_ONLY_ADMIN"
  | "THEME_MANAGER"
  | "ANALYST"
  | "VIEWER";

export interface User {
  id: string;
  email: string;
  fullName: string;
  role: "SUPER_ADMIN" | "SOCIETY_ADMIN" | TeamMemberRole;
  societyId: string | null;
  active: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface Society {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  country: string;
  unitCount: number;
  blockCount: number;
  plan: "free" | "starter" | "pro";
  activeThemeId?: string;
}

export interface Resident {
  id: string;
  societyId: string;
  unitId: string;
  unitNumber?: string;
  blockName?: string;
  fullName: string;
  email?: string;
  phone?: string;
  type: "owner" | "tenant" | "family";
  moveInDate?: string;
  moveOutDate?: string;
  active: boolean;
  username?: string;
  inviteSentAt?: string;
  lastLoginAt?: string;
}

export interface ResidentConsent {
  id: string;
  consentType: string;
  consentVersion: string;
  granted: boolean;
  grantedAt?: string;
  withdrawnAt?: string;
  source: string;
  recordedAt: string;
}

export interface PollOption {
  id: string;
  text: string;
}

export interface Poll {
  id: string;
  societyId: string;
  question: string;
  anonymous: boolean;
  closesAt?: string;
  status: "OPEN" | "CLOSED";
  createdAt: string;
  options: PollOption[];
  myVoteOptionId?: string;
  hasVoted: boolean;
}

export interface PollOptionResult {
  optionId: string;
  text: string;
  voteCount: number;
  voterResidentIds: string[];
}

export interface PollResults {
  pollId: string;
  anonymous: boolean;
  totalVotes: number;
  results: PollOptionResult[];
}

export interface ParkingSlot {
  id: string;
  societyId: string;
  blockId?: string;
  slotNumber: string;
  type: "CAR" | "BIKE" | "VISITOR";
  status: "VACANT" | "ASSIGNED";
  assignedUnitId?: string;
  assignedResidentId?: string;
  vehicleNumber?: string;
}

export interface Helper {
  id: string;
  societyId: string;
  name: string;
  phone: string;
  role: "MAID" | "DRIVER" | "COOK" | "OTHER";
  unitId?: string;
  active: boolean;
  photoUrl?: string;
  backgroundCheckStatus: "PENDING" | "VERIFIED" | "REJECTED";
  idProofUrl?: string;
  verifiedBy?: string;
  verifiedAt?: string;
}

export interface VisitorBlacklistEntry {
  id: string;
  societyId: string;
  name: string;
  phone: string;
  reason?: string;
  addedBy?: string;
}

export interface Expense {
  id: string;
  societyId: string;
  category: string;
  vendor?: string;
  amount: number;
  expenseDate: string;
  description?: string;
  receiptBase64?: string;
  reconciled?: boolean;
  reconciledAt?: string;
  bankReference?: string;
}

export interface LedgerEntry {
  id: string;
  date: string;
  type: "INCOME" | "EXPENSE";
  particulars: string;
  amount: number;
  runningBalance: number;
}

export interface BalanceSheet {
  cashOnHand: number;
  duesReceivable: number;
  totalFundBalance: number;
  totalIncome: number;
  totalExpense: number;
}

export interface Budget {
  id: string;
  societyId: string;
  category: string;
  period: string;
  budgetedAmount: number;
}

export interface BudgetVarianceCategory {
  category: string;
  budgetedAmount: number;
  actualAmount: number;
  variance: number;
  overBudget: boolean;
}

export interface BudgetVariance {
  period: string;
  categories: BudgetVarianceCategory[];
}

export interface Fund {
  id: string;
  societyId: string;
  name: string;
  type: "SINKING" | "CORPUS" | "MAINTENANCE" | "OTHER";
  balance: number;
}

export interface FundTransactionEntry {
  id: string;
  fundId: string;
  type: "CONTRIBUTION" | "WITHDRAWAL";
  amount: number;
  transactionDate: string;
  note?: string;
}

export interface MoveRequest {
  id: string;
  societyId: string;
  unitId: string;
  residentId: string;
  type: "MOVE_IN" | "MOVE_OUT";
  status: "PENDING" | "APPROVED" | "REJECTED";
  requestedDate?: string;
  actualDate?: string;
  remarks?: string;
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
}

export interface Visitor {
  id: string;
  societyId: string;
  unitId?: string;
  unitNumber?: string;
  blockName?: string;
  residentId?: string;
  residentName?: string;
  name: string;
  phone?: string;
  purpose?: string;
  vehicleNumber?: string;
  entryTime?: string;
  exitTime?: string;
  appointmentTime?: string;
  status: "pending" | "approved" | "denied" | "exited" | "PRE_APPROVED";
  approvedBy?: string;
  qrToken?: string;
  otpCode?: string;
  photoBase64?: string;
  recurring?: boolean;
  recurrenceEndDate?: string;
}

export interface Delivery {
  id: string;
  societyId: string;
  residentId?: string;
  unitId?: string;
  carrier?: string;
  trackingCode?: string;
  description?: string;
  status: "arrived" | "notified" | "collected" | "returned";
  arrivedAt: string;
  collectedAt?: string;
}

export interface Facility {
  id: string;
  societyId: string;
  name: string;
  type: string;
  capacity?: number;
  bookingSlotsPerDay: number;
  openTime?: string;
  closeTime?: string;
  slotDurationMinutes: number;
  bookingFee: number;
  depositAmount: number;
  active: boolean;
  peakStartTime?: string;
  peakEndTime?: string;
  peakBookingFee?: number;
  cooldownMinutes?: number;
  additionalWindows?: { openTime: string; closeTime: string }[];
}

export interface FacilityBooking {
  id: string;
  facilityId: string;
  residentId: string;
  bookedDate: string;
  slotStart: string;
  slotEnd: string;
  status: "confirmed" | "cancelled" | "completed";
  amount: number;
  depositAmount: number;
  paymentStatus: "pending" | "paid" | "waived" | "refunded";
}

export interface FacilityBlackoutDate {
  id: string;
  facilityId: string;
  blackoutDate: string;
  reason?: string;
}

export interface SlotAvailability {
  slotStart: string;
  slotEnd: string;
  available: boolean;
}

export interface FacilityAvailability {
  date: string;
  blackedOut: boolean;
  blackoutReason?: string;
  slots: SlotAvailability[];
}

export interface Invoice {
  id: string;
  societyId: string;
  unitId: string;
  unitNumber?: string;
  blockName?: string;
  residentId?: string;
  residentName?: string;
  invoiceNumber: string;
  amount: number;
  dueDate: string;
  paidAt?: string;
  status: "unpaid" | "paid" | "overdue" | "waived";
  lineItems: unknown[];
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  totalAmount?: number;
  lateFeeAmount?: number;
  overdueSince?: string;
  paymentClaimedAt?: string;
  paymentClaimedNote?: string;
}

export interface Transaction {
  id: string;
  invoiceId?: string;
  societyId: string;
  amount: number;
  type: "credit" | "debit";
  method?: string;
  referenceId?: string;
  note?: string;
  createdAt: string;
  reconciled?: boolean;
  reconciledAt?: string;
  bankReference?: string;
}

export interface Ticket {
  id: string;
  societyId: string;
  residentId?: string;
  raisedBy?: string;
  title: string;
  description?: string;
  category?: string;
  priority: "low" | "medium" | "high" | "urgent";
  status: "open" | "in-progress" | "resolved" | "closed";
  assignedTo?: string;
  resolvedAt?: string;
  blockId?: string;
  dueAt?: string;
  slaBreached: boolean;
  escalatedAt?: string;
  vendorId?: string;
  quoteAmount?: number;
  quoteStatus?: "requested" | "received" | "accepted" | "rejected";
  createdAt: string;
  updatedAt: string;
  residentName?: string;
  residentPhone?: string;
  unitNumber?: string;
  blockName?: string;
}

export interface TicketComment {
  id: string;
  ticketId: string;
  authorId: string;
  body: string;
  createdAt: string;
}

export interface TicketAttachment {
  id: string;
  ticketId: string;
  url: string;
  fileType: "image" | "video";
  uploadedBy?: string;
  createdAt: string;
}

export interface SlaConfig {
  id: string;
  societyId: string;
  category?: string;
  priority: string;
  responseMinutes?: number;
  resolutionMinutes: number;
}

export interface TicketAssignmentRule {
  id: string;
  societyId: string;
  category: string;
  blockId?: string;
  assignToUserId: string;
}

export interface MaintenanceRequest {
  id: string;
  societyId: string;
  unitId?: string;
  requestedBy?: string;
  title: string;
  description?: string;
  category?: string;
  status: "pending" | "scheduled" | "in-progress" | "completed" | "cancelled";
  scheduledAt?: string;
  completedAt?: string;
  cost?: number;
  unitNumber?: string;
  blockName?: string;
  residentName?: string;
  residentPhone?: string;
}

export interface Vendor {
  id: string;
  societyId: string;
  name: string;
  category?: string;
  phone?: string;
  email?: string;
  active: boolean;
  address?: string;
  gstin?: string;
  contactPerson?: string;
  bankDetails?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  contractDocUrl?: string;
  notes?: string;
}

export interface Staff {
  id: string;
  societyId: string;
  name: string;
  phone?: string;
  photoUrl?: string;
  category: "SECURITY" | "HOUSEKEEPING" | "GARDENING" | "OTHER";
  employmentType: "FULL_TIME" | "PART_TIME" | "CONTRACT";
  joiningDate?: string;
  salary: number;
  active: boolean;
  userId?: string;
}

export interface StaffAttendance {
  id: string;
  staffId: string;
  attendanceDate: string;
  status: "PRESENT" | "ABSENT" | "HALF_DAY" | "ON_LEAVE";
  checkInTime?: string;
  checkOutTime?: string;
  markedBy?: string;
  checkInSelfieUrl?: string;
  checkOutSelfieUrl?: string;
}

export interface LeaveRequest {
  id: string;
  staffId: string;
  leaveType: "SICK" | "CASUAL" | "EARNED" | "OTHER";
  fromDate: string;
  toDate: string;
  reason?: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  approvedBy?: string;
}

export interface ShiftSchedule {
  id: string;
  staffId: string;
  shiftDate: string;
  shiftType: "MORNING" | "EVENING" | "NIGHT" | "CUSTOM";
  startTime?: string;
  endTime?: string;
  notes?: string;
}

export interface Payslip {
  id: string;
  staffId: string;
  month: number;
  year: number;
  basicPay: number;
  deductions: number;
  bonus: number;
  netPay: number;
  status: "DRAFT" | "GENERATED" | "PAID";
  generatedAt?: string;
}

export interface PurchaseOrder {
  id: string;
  societyId: string;
  vendorId: string;
  description?: string;
  amount: number;
  status: "DRAFT" | "RAISED" | "GRN_RECEIVED" | "PAID";
  raisedBy?: string;
  raisedAt?: string;
  grnAt?: string;
  grnNotes?: string;
  grnReceivedBy?: string;
  paidAt?: string;
  paymentReference?: string;
}

export interface InventoryItem {
  id: string;
  societyId: string;
  name: string;
  category: "CONSUMABLE" | "ASSET";
  unit?: string;
  quantity: number;
  reorderLevel: number;
}

export interface InventoryStockTransaction {
  id: string;
  itemId: string;
  type: "IN" | "OUT";
  quantity: number;
  transactionDate: string;
  note?: string;
  relatedPoId?: string;
}

export interface Asset {
  id: string;
  societyId: string;
  name: string;
  category?: string;
  location?: string;
  purchaseDate?: string;
  installDate?: string;
}

export interface PmSchedule {
  id: string;
  assetId: string;
  frequencyDays: number;
  nextDueDate: string;
  lastCompletedDate?: string;
  vendorId?: string;
  instructions?: string;
}

export interface AssetMaintenanceLog {
  id: string;
  assetId: string;
  performedDate: string;
  performedBy?: string;
  vendorId?: string;
  notes?: string;
  cost?: number;
}

export interface AmcContract {
  id: string;
  societyId: string;
  vendorId: string;
  assetId?: string;
  description?: string;
  startDate: string;
  endDate: string;
  amount?: number;
}

export interface Notice {
  id: string;
  societyId: string;
  postedBy: string;
  title: string;
  body: string;
  pinned: boolean;
  expiresAt?: string;
  createdAt: string;
  notifyWhatsapp?: boolean;
  emergency?: boolean;
}

export interface NoticeReceiptSummary {
  totalRecipients: number;
  readCount: number;
  readResidentIds: string[];
}

export interface SocietyEvent {
  id: string;
  societyId: string;
  createdBy: string;
  title: string;
  description?: string;
  venue?: string;
  startsAt: string;
  endsAt?: string;
  maxAttendees?: number;
}

export interface SecurityLog {
  id: string;
  societyId: string;
  eventType: string;
  description?: string;
  severity: "info" | "warning" | "critical";
  location?: string;
  reportedBy?: string;
  createdAt: string;
  status?: "OPEN" | "RESOLVED";
  resolvedBy?: string;
  resolvedAt?: string;
}

export interface Theme {
  id: string;
  societyId?: string;
  name: string;
  slug: string;
  sidebarBackground: string;
  sidebarTextColor: string;
  topbarBackground: string;
  topbarTextColor: string;
  primaryAccent: string;
  surface: string;
  secondaryAccent?: string;
  cardBorder?: string;
  buttonStyle: string;
  layoutStyle: string;
  ctaText?: string;
  securityMode: string;
  analyticsScope: string;
  accessLevel: string;
  isActive: boolean;
}

export interface ThemePreview {
  id: string;
  name: string;
  slug: string;
  palette: {
    sidebarBackground: string;
    sidebarTextColor: string;
    topbarBackground: string;
    topbarTextColor: string;
    primaryAccent: string;
    surface: string;
  };
  config: {
    buttonStyle: string;
    layoutStyle: string;
    securityMode: string;
    analyticsScope: string;
  };
}

export interface AnalyticsOverview {
  totalUnits: number;
  occupiedUnits: number;
  activeResidents: number;
  visitorsToday: number;
  pendingVisitors: number;
  openTickets: number;
  resolvedToday: number;
  duesCollected: number;
  duesTarget: number;
  collectionRate: number;
  pendingDuesCount: number;
  pendingDuesAmount: number;
}

export interface AnalyticsTrend {
  month: string;
  duesCollected: number;
  visitors: number;
  tickets: number;
}

/* ─── Token response normalizer ─────────────────────────────────────────── */

// The login data object as the backend actually sends it (after envelope unwrap)
interface RawLoginData {
  accessToken?: string;
  access_token?: string;
  refreshToken?: string;
  refresh_token?: string;
  token?: string;
  userId?: string;
  email?: string;
  fullName?: string;
  full_name?: string;
  role?: string;
  societyId?: string | null;
  society_id?: string | null;
}

export interface LoginResult {
  accessToken: string;
  refreshToken: string;
  // Inline user fields returned by the backend on login
  userId?: string;
  email?: string;
  fullName?: string;
  role?: string;
  societyId?: string | null;
}

function normalizeLogin(raw: RawLoginData): LoginResult {
  const accessToken = raw.accessToken || raw.access_token || raw.token || "";
  const refreshToken = raw.refreshToken || raw.refresh_token || "";
  if (!accessToken)
    throw new Error("Server did not return an access token. Please try again.");
  return {
    accessToken,
    refreshToken,
    userId: raw.userId,
    email: raw.email,
    fullName: raw.fullName || raw.full_name,
    role: raw.role,
    societyId: raw.societyId ?? raw.society_id ?? null,
  };
}

/* ─── Auth ──────────────────────────────────────────────────────────────── */

export interface RequestAccessPayload {
  contactName: string;
  societyName: string;
  city?: string;
  phone?: string;
  email: string;
}

export interface InviteInfo {
  email: string;
  contactName?: string;
  plan: string;
  societyName: string;
  societyId: string;
}

export interface ResidentInviteInfo {
  fullName: string;
  email: string;
  societyName: string;
  societyCode: string;
  usernameAlreadySet: boolean;
}

export const teamMembers = {
  list: (sid: string) => getList<User>(`/api/societies/${sid}/team-members`),
  create: (
    sid: string,
    data: {
      email: string;
      password: string;
      fullName: string;
      role: TeamMemberRole;
    },
  ) => post<User>(`/api/societies/${sid}/team-members`, data),
  update: (
    sid: string,
    id: string,
    data: Partial<{ fullName: string; role: TeamMemberRole; active: boolean }>,
  ) => patch<User>(`/api/societies/${sid}/team-members/${id}`, data),
  remove: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/team-members/${id}`),
};

export const auth = {
  validateInvite: (token: string) =>
    request<InviteInfo>(
      "GET",
      `/api/v1/auth/validate-invite?token=${encodeURIComponent(token)}`,
      undefined,
      false,
    ),

  requestAccess: (data: RequestAccessPayload) =>
    request<void>("POST", "/api/v1/auth/request-access", data, false),

  validateResidentInvite: (token: string) =>
    request<ResidentInviteInfo>(
      "GET",
      `/api/v1/auth/validate-resident-invite?token=${encodeURIComponent(token)}`,
      undefined,
      false,
    ),

  completeResidentInvite: (data: {
    token: string;
    username: string;
    password: string;
    consentAccepted: boolean;
  }) =>
    request<void>("POST", "/api/v1/auth/complete-resident-invite", data, false),

  getMyConsent: () =>
    get<ResidentConsent[]>("/api/v1/auth/me/consent"),

  getMyConsentHistory: () =>
    get<ResidentConsent[]>("/api/v1/auth/me/consent/history"),

  grantMyConsent: (consentType: string) =>
    post<ResidentConsent>("/api/v1/auth/me/consent/grant", { consentType }),

  withdrawMyConsent: (consentType: string) =>
    post<ResidentConsent>("/api/v1/auth/me/consent/withdraw", { consentType }),

  login: async (email: string, password: string): Promise<LoginResult> => {
    tokenStore.clear();
    const raw = await request<RawLoginData>(
      "POST",
      "/api/v1/auth/login",
      { email, password },
      false,
    );
    return normalizeLogin(raw);
  },

  register: (data: {
    email: string;
    password: string;
    fullName: string;
    inviteToken?: string;
    role?: string;
    societyId?: string;
  }) =>
    request<{ user: User }>(
      "POST",
      "/api/v1/auth/register-invite",
      data,
      false,
    ),

  refresh: async (refreshToken: string): Promise<{ accessToken: string }> => {
    const raw = await post<RawLoginData>("/api/v1/auth/refresh", {
      refreshToken,
    });
    const accessToken = raw.accessToken || raw.access_token || raw.token || "";
    if (!accessToken)
      throw new Error(
        "Server did not return an access token. Please try again.",
      );
    tokenStore.setAccess(accessToken);
    // Refresh tokens rotate server-side on every use — persist the new one too,
    // or the next refresh attempt will reuse the now-revoked old token and fail.
    const newRefreshToken = raw.refreshToken || raw.refresh_token;
    if (newRefreshToken) tokenStore.setRefresh(newRefreshToken);
    return { accessToken };
  },

  revoke: (refreshToken: string) =>
    post<void>("/api/v1/auth/revoke", { refreshToken }),

  me: () => get<User>("/api/v1/auth/me"),

  checkAccessRequest: (email: string) =>
    request<{ pending: boolean }>(
      "GET",
      `/api/v1/auth/request-access/status?email=${encodeURIComponent(email)}`,
      undefined,
      false,
    ),

  requestPasswordReset: (email: string, societyCode?: string) =>
    request<void>(
      "POST",
      "/api/v1/auth/forgot-password",
      { email, ...(societyCode ? { societyCode } : {}) },
      false,
    ),

  resetPassword: (token: string, newPassword: string) =>
    request<void>(
      "POST",
      "/api/v1/auth/reset-password",
      { token, newPassword },
      false,
    ),

  updateProfile: (data: { fullName: string }) =>
    patch<{
      id: string;
      fullName: string;
      email: string;
      phone: string | null;
    }>("/api/v1/auth/me", data),

  requestEmailChangeOtp: (newEmail: string) =>
    post<void>("/api/v1/auth/profile/email/request-otp", { newEmail }),

  confirmEmailChange: (newEmail: string, otp: string) =>
    post<{ id: string; fullName: string; email: string; phone: string | null }>(
      "/api/v1/auth/profile/email/confirm",
      { newEmail, otp },
    ),

  requestPhoneChangeOtp: (newPhone: string) =>
    post<void>("/api/v1/auth/profile/phone/request-otp", { newPhone }),

  confirmPhoneChange: (newPhone: string, otp: string) =>
    post<{ id: string; fullName: string; email: string; phone: string | null }>(
      "/api/v1/auth/profile/phone/confirm",
      { newPhone, otp },
    ),

  exportMyData: () =>
    get<Record<string, unknown>>("/api/v1/auth/account/data-export"),

  requestAccountDeletionOtp: () =>
    post<void>("/api/v1/auth/account/delete-otp"),

  deleteAccount: (otp: string) =>
    request<void>("DELETE", "/api/v1/auth/account", { otp }),

  getMyAccessLogs: (page = 0, size = 20) =>
    getPage<AccessLogEntry>(`/api/v1/auth/me/access-logs?page=${page}&size=${size}`),
};

export interface AccessLogEntry {
  id: string;
  action: string;
  targetType?: string;
  targetId?: string;
  createdAt: string;
}

/* ─── Societies ─────────────────────────────────────────────────────────── */

export const societies = {
  list: () => getList<Society>("/api/societies"),
  create: (data: Partial<Society>) => post<Society>("/api/societies", data),
  get: (id: string) => get<Society>(`/api/societies/${id}`),
  update: (id: string, data: Partial<Society>) =>
    patch<Society>(`/api/societies/${id}`, data),
};

/* ─── Residents ─────────────────────────────────────────────────────────── */

export const residents = {
  list: (
    sid: string,
    params: {
      page?: number;
      size?: number;
      sort?: string;
      q?: string;
      status?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    qs.set("page", String(params.page ?? 0));
    qs.set("size", String(params.size ?? 20));
    if (params.sort) qs.set("sort", params.sort);
    if (params.q) qs.set("q", params.q);
    if (params.status) qs.set("status", params.status);
    return getPage<Resident>(
      `/api/societies/${sid}/residents?${qs.toString()}`,
    );
  },
  exportCsv: (sid: string, sort?: string) =>
    downloadFile(`/api/societies/${sid}/residents/export${sort ? `?sort=${encodeURIComponent(sort)}` : ""}`),
  create: (sid: string, data: Partial<Resident>) =>
    post<Resident>(`/api/societies/${sid}/residents`, data),
  get: (sid: string, id: string) =>
    get<Resident>(`/api/societies/${sid}/residents/${id}`),
  update: (sid: string, id: string, data: Partial<Resident>) =>
    patch<Resident>(`/api/societies/${sid}/residents/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/residents/${id}`),
  bulkDelete: (sid: string, ids: string[]) =>
    post<void>(`/api/societies/${sid}/residents/bulk-delete`, ids),
  sendInvite: (
    sid: string,
    id: string,
    data: { secretaryEmail: string; secretaryPassword: string },
  ) => post<Resident>(`/api/societies/${sid}/residents/${id}/invite`, data),
  bulkSendInvites: (
    sid: string,
    data: {
      residentIds: string[];
      secretaryEmail: string;
      secretaryPassword: string;
    },
  ) =>
    post<{ count: number }>(
      `/api/societies/${sid}/residents/invites/bulk`,
      data,
    ),
  getConsentHistory: (sid: string, id: string) =>
    get<ResidentConsent[]>(`/api/societies/${sid}/residents/${id}/consent`),
};

/* ─── Helpers ────────────────────────────────────────────────────────────── */

export const helpers = {
  list: (sid: string) => getList<Helper>(`/api/societies/${sid}/helpers`),
  create: (sid: string, data: Partial<Helper>) =>
    post<Helper>(`/api/societies/${sid}/helpers`, data),
  update: (sid: string, id: string, data: Partial<Helper>) =>
    patch<Helper>(`/api/societies/${sid}/helpers/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/helpers/${id}`),
  verify: (sid: string, id: string, status: Helper["backgroundCheckStatus"]) =>
    patch<Helper>(`/api/societies/${sid}/helpers/${id}/verify`, { status }),
};

/* ─── Visitor Blacklist ─────────────────────────────────────────────────── */

export const visitorBlacklist = {
  list: (sid: string) =>
    getList<VisitorBlacklistEntry>(`/api/societies/${sid}/visitor-blacklist`),
  create: (
    sid: string,
    data: { name: string; phone: string; reason?: string },
  ) =>
    post<VisitorBlacklistEntry>(
      `/api/societies/${sid}/visitor-blacklist`,
      data,
    ),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/visitor-blacklist/${id}`),
};

/* ─── Funds ──────────────────────────────────────────────────────────────── */

export const funds = {
  list: (sid: string) => getList<Fund>(`/api/societies/${sid}/funds`),
  create: (sid: string, data: { name: string; type: string }) =>
    post<Fund>(`/api/societies/${sid}/funds`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/funds/${id}`),
  listTransactions: (sid: string, fundId: string) =>
    getList<FundTransactionEntry>(
      `/api/societies/${sid}/funds/${fundId}/transactions`,
    ),
  recordTransaction: (
    sid: string,
    fundId: string,
    data: {
      type: string;
      amount: number;
      transactionDate: string;
      note?: string;
    },
  ) => post<Fund>(`/api/societies/${sid}/funds/${fundId}/transactions`, data),
};

/* ─── Budgets ────────────────────────────────────────────────────────────── */

export const budgets = {
  list: (sid: string) => getList<Budget>(`/api/societies/${sid}/budgets`),
  create: (
    sid: string,
    data: { category: string; period: string; budgetedAmount: number },
  ) => post<Budget>(`/api/societies/${sid}/budgets`, data),
  update: (
    sid: string,
    id: string,
    data: { category: string; period: string; budgetedAmount: number },
  ) => put<Budget>(`/api/societies/${sid}/budgets/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/budgets/${id}`),
  getVariance: (sid: string, period: string) =>
    get<BudgetVariance>(
      `/api/societies/${sid}/budgets/variance?period=${period}`,
    ),
};

/* ─── Ledger ─────────────────────────────────────────────────────────────── */

export const ledger = {
  getLedger: (sid: string) =>
    getList<LedgerEntry>(`/api/societies/${sid}/ledger`),
  getBalanceSheet: (sid: string) =>
    get<BalanceSheet>(`/api/societies/${sid}/ledger/balance-sheet`),
  exportCsv: async (sid: string): Promise<Blob> => {
    const token = tokenStore.getAccess();
    const res = await fetch(`/api/societies/${sid}/ledger/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error("Failed to export ledger");
    return res.blob();
  },
};

/* ─── Expenses ───────────────────────────────────────────────────────────── */

export const expenses = {
  list: (sid: string) => getList<Expense>(`/api/societies/${sid}/expenses`),
  create: (sid: string, data: Partial<Expense>) =>
    post<Expense>(`/api/societies/${sid}/expenses`, data),
  update: (sid: string, id: string, data: Partial<Expense>) =>
    put<Expense>(`/api/societies/${sid}/expenses/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/expenses/${id}`),
  reconcile: (sid: string, id: string, bankReference: string) =>
    patch<Expense>(`/api/societies/${sid}/expenses/${id}/reconcile`, {
      bankReference,
    }),
};

/* ─── Move Requests ─────────────────────────────────────────────────────── */

type MoveRequestPayload = {
  type: "MOVE_IN" | "MOVE_OUT";
  residentId?: string;
  requestedDate?: string;
  remarks?: string;
};

export const moveRequests = {
  list: (sid: string, unitId: string) =>
    getList<MoveRequest>(`/api/societies/${sid}/units/${unitId}/move-requests`),
  // Society-wide listing (admin approvals inbox) — every unit's requests in one call.
  listAll: (sid: string) =>
    getList<MoveRequest>(`/api/societies/${sid}/move-requests`),
  create: (sid: string, unitId: string, data: MoveRequestPayload) =>
    post<MoveRequest>(
      `/api/societies/${sid}/units/${unitId}/move-requests`,
      data,
    ),
  // Unit-agnostic approve/reject/update/delete — the request already carries its own
  // unitId, so these don't need it in the path.
  approve: (sid: string, id: string) =>
    patch<MoveRequest>(`/api/societies/${sid}/move-requests/${id}/approve`),
  reject: (sid: string, id: string) =>
    patch<MoveRequest>(`/api/societies/${sid}/move-requests/${id}/reject`),
  update: (sid: string, id: string, data: MoveRequestPayload) =>
    patch<MoveRequest>(`/api/societies/${sid}/move-requests/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/move-requests/${id}`),
  downloadNoc: async (
    sid: string,
    unitId: string,
    id: string,
  ): Promise<Blob> => {
    const token = tokenStore.getAccess();
    const res = await fetch(
      `/api/societies/${sid}/units/${unitId}/move-requests/${id}/noc`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );
    if (!res.ok) throw new Error("Failed to download NOC");
    return res.blob();
  },
};

/* ─── Visitors ──────────────────────────────────────────────────────────── */

export const visitors = {
  list: (
    sid: string,
    params: {
      page?: number;
      size?: number;
      sort?: string;
      q?: string;
      status?: string;
      date?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    qs.set("page", String(params.page ?? 0));
    qs.set("size", String(params.size ?? 20));
    if (params.sort) qs.set("sort", params.sort);
    if (params.q) qs.set("q", params.q);
    if (params.status) qs.set("status", params.status);
    if (params.date) qs.set("date", params.date);
    return getPage<Visitor>(`/api/societies/${sid}/visitors?${qs.toString()}`);
  },
  exportCsv: (sid: string, sort?: string) =>
    downloadFile(`/api/societies/${sid}/visitors/export${sort ? `?sort=${encodeURIComponent(sort)}` : ""}`),
  create: (sid: string, data: Partial<Visitor>) =>
    post<Visitor>(`/api/societies/${sid}/visitors`, data),
  preApprove: (sid: string, data: Partial<Visitor>) =>
    post<Visitor>(`/api/societies/${sid}/visitors/pre-approve`, data),
  checkIn: (sid: string, data: { qrToken?: string; otpCode?: string }) =>
    post<Visitor>(`/api/societies/${sid}/visitors/check-in`, data),
  approve: (sid: string, id: string) =>
    patch<Visitor>(`/api/societies/${sid}/visitors/${id}/approve`),
  deny: (sid: string, id: string) =>
    patch<Visitor>(`/api/societies/${sid}/visitors/${id}/deny`),
  exit: (sid: string, id: string) =>
    patch<Visitor>(`/api/societies/${sid}/visitors/${id}/exit`),
  update: (sid: string, id: string, data: Partial<Visitor>) =>
    patch<Visitor>(`/api/societies/${sid}/visitors/${id}`, data),
  remove: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/visitors/${id}`),
};

/* ─── Deliveries ────────────────────────────────────────────────────────── */

export const deliveries = {
  list: (sid: string) => getList<Delivery>(`/api/societies/${sid}/deliveries`),
  listPage: (
    sid: string,
    params: {
      page?: number;
      size?: number;
      sort?: string;
      status?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    qs.set("page", String(params.page ?? 0));
    qs.set("size", String(params.size ?? 20));
    if (params.sort) qs.set("sort", params.sort);
    if (params.status) qs.set("status", params.status);
    return getPage<Delivery>(
      `/api/societies/${sid}/deliveries?${qs.toString()}`,
    );
  },
  create: (sid: string, data: Partial<Delivery>) =>
    post<Delivery>(`/api/societies/${sid}/deliveries`, data),
  update: (sid: string, id: string, data: Partial<Delivery>) =>
    patch<Delivery>(`/api/societies/${sid}/deliveries/${id}`, data),
  updateStatus: (sid: string, id: string, status: Delivery["status"]) =>
    patch<Delivery>(`/api/societies/${sid}/deliveries/${id}/status`, {
      status,
    }),
};

/* ─── Facilities ────────────────────────────────────────────────────────── */

export const facilities = {
  list: (sid: string) => getList<Facility>(`/api/societies/${sid}/facilities`),
  create: (sid: string, data: Partial<Facility>) =>
    post<Facility>(`/api/societies/${sid}/facilities`, data),
  update: (sid: string, fid: string, data: Partial<Facility>) =>
    patch<Facility>(`/api/societies/${sid}/facilities/${fid}`, data),
  delete: (sid: string, fid: string) =>
    del<void>(`/api/societies/${sid}/facilities/${fid}`),
  getAvailability: (sid: string, fid: string, date: string) =>
    get<FacilityAvailability>(
      `/api/societies/${sid}/facilities/${fid}/availability?date=${date}`,
    ),
  listBookings: (sid: string, fid: string) =>
    get<FacilityBooking[]>(`/api/societies/${sid}/facilities/${fid}/bookings`),
  createBooking: (sid: string, fid: string, data: Partial<FacilityBooking>) =>
    post<FacilityBooking>(
      `/api/societies/${sid}/facilities/${fid}/bookings`,
      data,
    ),
  deleteBooking: (sid: string, fid: string, bid: string) =>
    del<void>(`/api/societies/${sid}/facilities/${fid}/bookings/${bid}`),
  createPaymentOrder: (sid: string, fid: string, bid: string) =>
    post<PaymentOrder>(
      `/api/societies/${sid}/facilities/${fid}/bookings/${bid}/payment-order`,
    ),
  verifyPayment: (
    sid: string,
    fid: string,
    bid: string,
    data: { razorpayPaymentId: string; razorpaySignature: string },
  ) =>
    post<FacilityBooking>(
      `/api/societies/${sid}/facilities/${fid}/bookings/${bid}/payment-verify`,
      data,
    ),
  listBlackoutDates: (sid: string, fid: string) =>
    getList<FacilityBlackoutDate>(
      `/api/societies/${sid}/facilities/${fid}/blackout-dates`,
    ),
  addBlackoutDate: (
    sid: string,
    fid: string,
    data: Partial<FacilityBlackoutDate>,
  ) =>
    post<FacilityBlackoutDate>(
      `/api/societies/${sid}/facilities/${fid}/blackout-dates`,
      data,
    ),
  deleteBlackoutDate: (sid: string, fid: string, bdid: string) =>
    del<void>(`/api/societies/${sid}/facilities/${fid}/blackout-dates/${bdid}`),
};

/* ─── Billing ───────────────────────────────────────────────────────────── */

export const billing = {
  listInvoices: (sid: string) =>
    getList<Invoice>(`/api/societies/${sid}/invoices`),
  listInvoicesPage: (
    sid: string,
    params: { page?: number; size?: number; sort?: string } = {},
  ) => {
    const qs = new URLSearchParams();
    qs.set("page", String(params.page ?? 0));
    qs.set("size", String(params.size ?? 20));
    if (params.sort) qs.set("sort", params.sort);
    return getPage<Invoice>(`/api/societies/${sid}/invoices?${qs.toString()}`);
  },
  createInvoice: (sid: string, data: Partial<Invoice>) =>
    post<Invoice>(`/api/societies/${sid}/invoices`, data),
  getInvoice: (sid: string, id: string) =>
    get<Invoice>(`/api/societies/${sid}/invoices/${id}`),
  payInvoice: (
    sid: string,
    id: string,
    data?: { method?: string; referenceId?: string },
  ) => patch<Invoice>(`/api/societies/${sid}/invoices/${id}/pay`, data),
  confirmClaimedPayment: (sid: string, id: string) =>
    patch<Invoice>(`/api/societies/${sid}/invoices/${id}/confirm-payment`),
  rejectClaimedPayment: (sid: string, id: string, note?: string) =>
    patch<Invoice>(
      `/api/societies/${sid}/invoices/${id}/reject-payment`,
      note ? { note } : undefined,
    ),
  updateInvoice: (sid: string, id: string, data: Partial<Invoice>) =>
    patch<Invoice>(`/api/societies/${sid}/invoices/${id}`, data),
  deleteInvoice: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/invoices/${id}`),
  listTransactions: (sid: string) =>
    getList<Transaction>(`/api/societies/${sid}/transactions`),
  createPaymentOrder: (sid: string, id: string) =>
    postIdempotent<PaymentOrder>(
      `/api/societies/${sid}/invoices/${id}/payment-order`,
    ),
  verifyPayment: (
    sid: string,
    id: string,
    data: { razorpayPaymentId: string; razorpaySignature: string },
  ) =>
    postIdempotent<Invoice>(
      `/api/societies/${sid}/invoices/${id}/payment-verify`,
      data,
    ),
  reconcileTransaction: (sid: string, id: string, bankReference: string) =>
    patch<Transaction>(`/api/societies/${sid}/transactions/${id}/reconcile`, {
      bankReference,
    }),
};

export interface PaymentOrder {
  orderId: string;
  keyId: string;
  amount: number;
  currency: string;
}

/* ─── Social Feed ───────────────────────────────────────────────────────── */

export interface Post {
  id: string;
  authorId: string;
  authorName: string;
  content: string;
  photoBase64?: string;
  likeCount: number;
  commentCount: number;
  likedByMe: boolean;
  mine: boolean;
  createdAt: string;
}

export interface PostComment {
  id: string;
  authorId: string;
  authorName: string;
  content: string;
  mine: boolean;
  createdAt: string;
}

export const feed = {
  listPosts: (sid: string, page = 0, size = 20) =>
    getPage<Post>(
      `/api/societies/${sid}/posts?page=${page}&size=${size}&sort=createdAt,desc`,
    ),
  createPost: (sid: string, data: { content: string; photoBase64?: string }) =>
    post<Post>(`/api/societies/${sid}/posts`, data),
  deletePost: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/posts/${id}`),
  toggleLike: (sid: string, id: string) =>
    post<Post>(`/api/societies/${sid}/posts/${id}/like`),
  listComments: (sid: string, id: string) =>
    getList<PostComment>(`/api/societies/${sid}/posts/${id}/comments`),
  createComment: (sid: string, id: string, content: string) =>
    post<PostComment>(`/api/societies/${sid}/posts/${id}/comments`, {
      content,
    }),
  deleteComment: (sid: string, id: string, commentId: string) =>
    del<void>(`/api/societies/${sid}/posts/${id}/comments/${commentId}`),
};

/* ─── Helpdesk ──────────────────────────────────────────────────────────── */

export const helpdesk = {
  list: (sid: string) => getList<Ticket>(`/api/societies/${sid}/tickets`),
  create: (sid: string, data: Partial<Ticket>) =>
    post<Ticket>(`/api/societies/${sid}/tickets`, data),
  get: (sid: string, id: string) =>
    get<Ticket>(`/api/societies/${sid}/tickets/${id}`),
  update: (sid: string, id: string, data: Partial<Ticket>) =>
    patch<Ticket>(`/api/societies/${sid}/tickets/${id}`, data),
  listComments: (sid: string, id: string) =>
    getList<TicketComment>(`/api/societies/${sid}/tickets/${id}/comments`),
  addComment: (sid: string, id: string, body: string) =>
    post<TicketComment>(`/api/societies/${sid}/tickets/${id}/comments`, {
      body,
    }),
  listAttachments: (sid: string, id: string) =>
    getList<TicketAttachment>(
      `/api/societies/${sid}/tickets/${id}/attachments`,
    ),
  addAttachment: (
    sid: string,
    id: string,
    url: string,
    fileType: "image" | "video",
  ) =>
    post<TicketAttachment>(`/api/societies/${sid}/tickets/${id}/attachments`, {
      url,
      fileType,
    }),
};

export const slaConfigs = {
  list: (sid: string) =>
    getList<SlaConfig>(`/api/societies/${sid}/sla-configs`),
  create: (sid: string, data: Partial<SlaConfig>) =>
    post<SlaConfig>(`/api/societies/${sid}/sla-configs`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/sla-configs/${id}`),
};

export const assignmentRules = {
  list: (sid: string) =>
    getList<TicketAssignmentRule>(`/api/societies/${sid}/assignment-rules`),
  create: (sid: string, data: Partial<TicketAssignmentRule>) =>
    post<TicketAssignmentRule>(`/api/societies/${sid}/assignment-rules`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/assignment-rules/${id}`),
};

export const uploads = {
  upload: (sid: string, file: File) =>
    uploadFile<{ url: string }>(`/api/societies/${sid}/uploads`, file),
  download: (sid: string, fileUrl: string) =>
    downloadFile(
      `/api/societies/${sid}/uploads/download?url=${encodeURIComponent(fileUrl)}`,
    ),
};

/* ─── Documents ─────────────────────────────────────────────────────────── */

export interface NexoraDocument {
  id: string;
  societyId: string;
  title: string;
  category?: string;
  visibilityScope: "PERSONAL" | "SOCIETY" | "MANAGEMENT";
  unitId?: string;
  fileUrl: string;
  uploadedBy: string;
  createdAt: string;
}

export const documents = {
  list: (sid: string) =>
    getList<NexoraDocument>(`/api/societies/${sid}/documents`),
  create: (
    sid: string,
    data: {
      title: string;
      category?: string;
      visibilityScope: string;
      unitId?: string;
      fileUrl: string;
    },
  ) => post<NexoraDocument>(`/api/societies/${sid}/documents`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/documents/${id}`),
};

/* ─── Maintenance ───────────────────────────────────────────────────────── */

export const maintenance = {
  list: (sid: string) =>
    getList<MaintenanceRequest>(`/api/societies/${sid}/maintenance`),
  create: (sid: string, data: Partial<MaintenanceRequest>) =>
    post<MaintenanceRequest>(`/api/societies/${sid}/maintenance`, data),
  updateStatus: (
    sid: string,
    id: string,
    status: MaintenanceRequest["status"],
  ) =>
    patch<MaintenanceRequest>(
      `/api/societies/${sid}/maintenance/${id}/status`,
      { status },
    ),
};

export const vendors = {
  list: (sid: string) => getList<Vendor>(`/api/societies/${sid}/vendors`),
  create: (sid: string, data: Partial<Vendor>) =>
    post<Vendor>(`/api/societies/${sid}/vendors`, data),
  update: (sid: string, id: string, data: Partial<Vendor>) =>
    patch<Vendor>(`/api/societies/${sid}/vendors/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/vendors/${id}`),
};

export const assets = {
  list: (sid: string) => getList<Asset>(`/api/societies/${sid}/assets`),
  create: (sid: string, data: Partial<Asset>) =>
    post<Asset>(`/api/societies/${sid}/assets`, data),
  update: (sid: string, id: string, data: Partial<Asset>) =>
    patch<Asset>(`/api/societies/${sid}/assets/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/assets/${id}`),
  listPmSchedules: (sid: string, id: string) =>
    getList<PmSchedule>(`/api/societies/${sid}/assets/${id}/pm-schedules`),
  addPmSchedule: (sid: string, data: Partial<PmSchedule>) =>
    post<PmSchedule>(`/api/societies/${sid}/assets/pm-schedules`, data),
  completePmSchedule: (
    sid: string,
    assetId: string,
    scheduleId: string,
    data: {
      performedDate?: string;
      notes?: string;
      cost?: number;
      vendorId?: string;
    },
  ) =>
    patch<AssetMaintenanceLog>(
      `/api/societies/${sid}/assets/${assetId}/pm-schedules/${scheduleId}/complete`,
      data,
    ),
  listMaintenanceLogs: (sid: string, id: string) =>
    getList<AssetMaintenanceLog>(
      `/api/societies/${sid}/assets/${id}/maintenance-logs`,
    ),
};

export const amcContracts = {
  list: (sid: string) =>
    getList<AmcContract>(`/api/societies/${sid}/amc-contracts`),
  create: (sid: string, data: Partial<AmcContract>) =>
    post<AmcContract>(`/api/societies/${sid}/amc-contracts`, data),
  update: (sid: string, id: string, data: Partial<AmcContract>) =>
    patch<AmcContract>(`/api/societies/${sid}/amc-contracts/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/amc-contracts/${id}`),
};

export const purchaseOrders = {
  list: (sid: string) =>
    getList<PurchaseOrder>(`/api/societies/${sid}/purchase-orders`),
  create: (
    sid: string,
    data: { vendorId: string; description?: string; amount: number },
  ) => post<PurchaseOrder>(`/api/societies/${sid}/purchase-orders`, data),
  update: (
    sid: string,
    id: string,
    data: { vendorId: string; description?: string; amount: number },
  ) => patch<PurchaseOrder>(`/api/societies/${sid}/purchase-orders/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/purchase-orders/${id}`),
  raise: (sid: string, id: string) =>
    patch<PurchaseOrder>(`/api/societies/${sid}/purchase-orders/${id}/raise`),
  recordGrn: (sid: string, id: string, notes?: string) =>
    patch<PurchaseOrder>(`/api/societies/${sid}/purchase-orders/${id}/grn`, {
      notes,
    }),
  markPaid: (sid: string, id: string, paymentReference?: string) =>
    patch<PurchaseOrder>(`/api/societies/${sid}/purchase-orders/${id}/pay`, {
      paymentReference,
    }),
};

export const inventory = {
  listItems: (sid: string) =>
    getList<InventoryItem>(`/api/societies/${sid}/inventory-items`),
  createItem: (sid: string, data: Partial<InventoryItem>) =>
    post<InventoryItem>(`/api/societies/${sid}/inventory-items`, data),
  updateItem: (sid: string, id: string, data: Partial<InventoryItem>) =>
    patch<InventoryItem>(`/api/societies/${sid}/inventory-items/${id}`, data),
  deleteItem: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/inventory-items/${id}`),
  listTransactions: (sid: string, itemId: string) =>
    getList<InventoryStockTransaction>(
      `/api/societies/${sid}/inventory-items/${itemId}/transactions`,
    ),
  addTransaction: (
    sid: string,
    itemId: string,
    data: {
      type: "IN" | "OUT";
      quantity: number;
      transactionDate: string;
      note?: string;
      relatedPoId?: string;
    },
  ) =>
    post<InventoryStockTransaction>(
      `/api/societies/${sid}/inventory-items/${itemId}/transactions`,
      data,
    ),
};

/* ─── Staff & Payroll ───────────────────────────────────────────────────── */

export const staff = {
  list: (sid: string) => getList<Staff>(`/api/societies/${sid}/staff`),
  create: (sid: string, data: Partial<Staff>) =>
    post<Staff>(`/api/societies/${sid}/staff`, data),
  update: (sid: string, id: string, data: Partial<Staff>) =>
    patch<Staff>(`/api/societies/${sid}/staff/${id}`, data),
};

export const attendance = {
  listByStaff: (sid: string, staffId: string) =>
    getList<StaffAttendance>(
      `/api/societies/${sid}/staff/${staffId}/attendance`,
    ),
  listByDate: (sid: string, date: string) =>
    getList<StaffAttendance>(`/api/societies/${sid}/attendance?date=${date}`),
  mark: (
    sid: string,
    staffId: string,
    data: {
      attendanceDate: string;
      status: StaffAttendance["status"];
      checkInTime?: string;
      checkOutTime?: string;
      checkInSelfieUrl?: string;
      checkOutSelfieUrl?: string;
    },
  ) =>
    post<StaffAttendance>(
      `/api/societies/${sid}/staff/${staffId}/attendance`,
      data,
    ),
};

export const leaveRequests = {
  list: (sid: string) =>
    getList<LeaveRequest>(`/api/societies/${sid}/leave-requests`),
  create: (
    sid: string,
    data: {
      staffId: string;
      leaveType?: string;
      fromDate: string;
      toDate: string;
      reason?: string;
    },
  ) => post<LeaveRequest>(`/api/societies/${sid}/leave-requests`, data),
  decide: (sid: string, id: string, status: "APPROVED" | "REJECTED") =>
    patch<LeaveRequest>(`/api/societies/${sid}/leave-requests/${id}/decision`, {
      status,
    }),
};

export const shifts = {
  list: (sid: string) => getList<ShiftSchedule>(`/api/societies/${sid}/shifts`),
  create: (
    sid: string,
    data: {
      staffId: string;
      shiftDate: string;
      shiftType: ShiftSchedule["shiftType"];
      startTime?: string;
      endTime?: string;
      notes?: string;
    },
  ) => post<ShiftSchedule>(`/api/societies/${sid}/shifts`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/shifts/${id}`),
};

export const payroll = {
  list: (sid: string) => getList<Payslip>(`/api/societies/${sid}/payslips`),
  listByStaff: (sid: string, staffId: string) =>
    getList<Payslip>(`/api/societies/${sid}/staff/${staffId}/payslips`),
  generate: (
    sid: string,
    staffId: string,
    data: { month: number; year: number; deductions?: number; bonus?: number },
  ) => post<Payslip>(`/api/societies/${sid}/staff/${staffId}/payslips`, data),
  markPaid: (sid: string, id: string) =>
    patch<Payslip>(`/api/societies/${sid}/payslips/${id}/pay`),
};

/* ─── Notices ───────────────────────────────────────────────────────────── */

export const notices = {
  list: (sid: string) => getList<Notice>(`/api/societies/${sid}/notices`),
  create: (sid: string, data: Partial<Notice>) =>
    post<Notice>(`/api/societies/${sid}/notices`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/notices/${id}`),
  markRead: (sid: string, id: string) =>
    post<void>(`/api/societies/${sid}/notices/${id}/read`),
  getReceipts: (sid: string, id: string) =>
    get<NoticeReceiptSummary>(`/api/societies/${sid}/notices/${id}/receipts`),
};

/* ─── Events ────────────────────────────────────────────────────────────── */

export const events = {
  list: (sid: string) => getList<SocietyEvent>(`/api/societies/${sid}/events`),
  create: (sid: string, data: Partial<SocietyEvent>) =>
    post<SocietyEvent>(`/api/societies/${sid}/events`, data),
  rsvp: (
    sid: string,
    eventId: string,
    status: "going" | "not_going" | "maybe",
  ) => post<void>(`/api/societies/${sid}/events/${eventId}/rsvp`, { status }),
};

/* ─── Security ──────────────────────────────────────────────────────────── */

export const security = {
  listLogs: (sid: string) =>
    getList<SecurityLog>(`/api/societies/${sid}/security/logs`),
  createLog: (sid: string, data: Partial<SecurityLog>) =>
    post<SecurityLog>(`/api/societies/${sid}/security/logs`, data),
  resolve: (sid: string, id: string) =>
    patch<SecurityLog>(`/api/societies/${sid}/security/logs/${id}/resolve`),
  raiseSos: (sid: string) =>
    post<SecurityLog>(`/api/societies/${sid}/security/sos`),
  raiseChildSafetyAlert: (
    sid: string,
    data: { childName?: string; location?: string },
  ) => post<SecurityLog>(`/api/societies/${sid}/security/child-alert`, data),
};

/* ─── Themes ────────────────────────────────────────────────────────────── */

export const themes = {
  listAll: () => get<Theme[]>("/api/themes"),
  list: (sid: string) => getList<Theme>(`/api/societies/${sid}/themes`),
  getActive: (sid: string) => get<Theme | null>(`/api/societies/${sid}/themes/active`),
  create: (sid: string, data: Partial<Theme>) =>
    post<Theme>(`/api/societies/${sid}/themes`, data),
  get: (sid: string, id: string) =>
    get<Theme>(`/api/societies/${sid}/themes/${id}`),
  update: (sid: string, id: string, data: Partial<Theme>) =>
    put<Theme>(`/api/societies/${sid}/themes/${id}`, data),
  patch: (sid: string, id: string, data: Partial<Theme>) =>
    patch<Theme>(`/api/societies/${sid}/themes/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/themes/${id}`),
  activate: (sid: string, id: string) =>
    patch<void>(`/api/societies/${sid}/themes/${id}/activate`),
  preview: (sid: string, id: string) =>
    get<ThemePreview>(`/api/societies/${sid}/themes/${id}/preview`),
};

/* ─── Society Features (incl. resident contact policy) ─────────────────── */

export interface SocietyFeatures {
  noticeboardEnabled: boolean;
  eventsEnabled: boolean;
  requireResidentEmail: boolean;
  requireResidentPhone: boolean;
}

export const societyFeatures = {
  get: (sid: string) => get<SocietyFeatures>(`/api/societies/${sid}/features`),
};

/* ─── Blocks & Units ────────────────────────────────────────────────────── */

export const blocks = {
  list: (sid: string) => getList<Block>(`/api/societies/${sid}/blocks`),
  create: (sid: string, data: Partial<Block>) =>
    post<Block>(`/api/societies/${sid}/blocks`, data),
  update: (sid: string, id: string, data: Partial<Block>) =>
    patch<Block>(`/api/societies/${sid}/blocks/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/blocks/${id}`),
};

export const unitTypes = {
  list: (sid: string) => getList<UnitType>(`/api/societies/${sid}/unit-types`),
  create: (sid: string, data: Partial<UnitType>) =>
    post<UnitType>(`/api/societies/${sid}/unit-types`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/unit-types/${id}`),
};

export const units = {
  list: (
    sid: string,
    params: {
      page?: number;
      size?: number;
      sort?: string;
      q?: string;
      status?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    qs.set("page", String(params.page ?? 0));
    qs.set("size", String(params.size ?? 20));
    if (params.sort) qs.set("sort", params.sort);
    if (params.q) qs.set("q", params.q);
    if (params.status) qs.set("status", params.status);
    return getPage<Unit>(`/api/societies/${sid}/units?${qs.toString()}`);
  },
  get: (sid: string, id: string) =>
    get<Unit>(`/api/societies/${sid}/units/${id}`),
  create: (sid: string, data: Partial<Unit>) =>
    post<Unit>(`/api/societies/${sid}/units`, data),
  update: (sid: string, id: string, data: Partial<Unit>) =>
    patch<Unit>(`/api/societies/${sid}/units/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/units/${id}`),
  bulkDelete: (sid: string, ids: string[]) =>
    post<void>(`/api/societies/${sid}/units/bulk-delete`, ids),
  importCsv: (sid: string, file: File) =>
    uploadFile<CsvImportResult>(`/api/societies/${sid}/units/import`, file),
  exportCsv: (sid: string, sort?: string) =>
    downloadFile(`/api/societies/${sid}/units/export${sort ? `?sort=${encodeURIComponent(sort)}` : ""}`),
};

/* ─── Polls ──────────────────────────────────────────────────────────────── */

export const polls = {
  list: (sid: string) => getList<Poll>(`/api/societies/${sid}/polls`),
  create: (
    sid: string,
    data: {
      question: string;
      anonymous: boolean;
      closesAt?: string;
      options: string[];
    },
  ) => post<Poll>(`/api/societies/${sid}/polls`, data),
  vote: (sid: string, id: string, optionId: string) =>
    post<Poll>(`/api/societies/${sid}/polls/${id}/vote`, { optionId }),
  close: (sid: string, id: string) =>
    patch<Poll>(`/api/societies/${sid}/polls/${id}/close`),
  getResults: (sid: string, id: string) =>
    get<PollResults>(`/api/societies/${sid}/polls/${id}/results`),
};

/* ─── Parking Slots ─────────────────────────────────────────────────────── */

export const parkingSlots = {
  list: (
    sid: string,
    params: {
      page?: number;
      size?: number;
      sort?: string;
      q?: string;
      unitId?: string;
      residentId?: string;
      type?: string;
      status?: string;
      slotNumber?: string;
      vehicleNumber?: string;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    qs.set("page", String(params.page ?? 0));
    qs.set("size", String(params.size ?? 20));
    if (params.sort) qs.set("sort", params.sort);
    if (params.q) qs.set("q", params.q);
    if (params.unitId) qs.set("unitId", params.unitId);
    if (params.residentId) qs.set("residentId", params.residentId);
    if (params.type) qs.set("type", params.type);
    if (params.status) qs.set("status", params.status);
    if (params.slotNumber) qs.set("slotNumber", params.slotNumber);
    if (params.vehicleNumber) qs.set("vehicleNumber", params.vehicleNumber);
    return getPage<ParkingSlot>(
      `/api/societies/${sid}/parking-slots?${qs.toString()}`,
    );
  },
  create: (
    sid: string,
    data: { slotNumber: string; type?: string; blockId?: string },
  ) => post<ParkingSlot>(`/api/societies/${sid}/parking-slots`, data),
  update: (
    sid: string,
    id: string,
    data: { slotNumber: string; type?: string; blockId?: string },
  ) =>
    patch<ParkingSlot>(`/api/societies/${sid}/parking-slots/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/parking-slots/${id}`),
  bulkDelete: (sid: string, ids: string[]) =>
    post<void>(`/api/societies/${sid}/parking-slots/bulk-delete`, ids),
  assign: (
    sid: string,
    id: string,
    data: { unitId: string; residentId?: string; vehicleNumber?: string },
  ) =>
    patch<ParkingSlot>(
      `/api/societies/${sid}/parking-slots/${id}/assign`,
      data,
    ),
  unassign: (sid: string, id: string) =>
    patch<ParkingSlot>(`/api/societies/${sid}/parking-slots/${id}/unassign`),
  importCsv: (sid: string, file: File) =>
    uploadFile<CsvImportResult>(
      `/api/societies/${sid}/parking-slots/import`,
      file,
    ),
  exportCsv: (sid: string, sort?: string) =>
    downloadFile(`/api/societies/${sid}/parking-slots/export${sort ? `?sort=${encodeURIComponent(sort)}` : ""}`),
};

/* ─── Analytics ─────────────────────────────────────────────────────────── */

interface TrendsResponse {
  visitorsByMonth: Record<string, number>;
  ticketsByMonth: Record<string, number>;
  revenueByMonth: Record<string, number>;
}

export const analytics = {
  overview: (sid: string) =>
    get<AnalyticsOverview>(`/api/societies/${sid}/analytics/overview`),
  trends: async (sid: string): Promise<AnalyticsTrend[]> => {
    const res = await get<TrendsResponse>(
      `/api/societies/${sid}/analytics/trends`
    );
    const months = new Set([
      ...Object.keys(res.visitorsByMonth ?? {}),
      ...Object.keys(res.ticketsByMonth ?? {}),
      ...Object.keys(res.revenueByMonth ?? {}),
    ]);
    return Array.from(months)
      .sort()
      .map((month) => ({
        month,
        duesCollected: res.revenueByMonth?.[month] ?? 0,
        visitors: res.visitorsByMonth?.[month] ?? 0,
        tickets: res.ticketsByMonth?.[month] ?? 0,
      }));
  },
};

/* ─── Audit Logs ─────────────────────────────────────────────────────────── */

export interface AuditLogEntry {
  id: string;
  societyId?: string;
  userId?: string;
  action: string;
  targetType?: string;
  targetId?: string;
  payload?: string;
  createdAt: string;
}

export interface AuditLogPage {
  content: AuditLogEntry[];
  totalPages: number;
  totalElements: number;
  number: number;
}

export const auditLogs = {
  list: (
    sid: string,
    params: {
      action?: string;
      targetType?: string;
      page?: number;
      size?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.action) qs.set("action", params.action);
    if (params.targetType) qs.set("targetType", params.targetType);
    qs.set("page", String(params.page ?? 0));
    qs.set("size", String(params.size ?? 25));
    qs.set("sort", "createdAt,desc");
    return get<AuditLogPage>(
      `/api/societies/${sid}/audit-logs?${qs.toString()}`,
    );
  },
};

/* ─── Reports & Analytics (M8) ──────────────────────────────────────────── */

export interface FinancialReport {
  incomeByMonth: { month: string; amount: number }[];
  expensesByCategory: { category: string; amount: number }[];
  totalIncome: number;
  totalExpense: number;
  netSurplus: number;
}

export interface Defaulter {
  unitId: string;
  unitNumber?: string;
  residentName?: string;
  outstandingAmount: number;
  daysOverdue: number;
  dueDate?: string;
}

export interface CollectionReport {
  collectionRate: number;
  totalBilled: number;
  totalCollected: number;
  defaulters: Defaulter[];
}

export interface SlaCategoryCompliance {
  category: string;
  total: number;
  breached: number;
  complianceRate: number;
  avgResolutionHours?: number;
}

export interface SlaComplianceReport {
  categories: SlaCategoryCompliance[];
  totalTickets: number;
  totalBreached: number;
  overallComplianceRate: number;
}

export interface FacilityUsage {
  facilityId: string;
  facilityName: string;
  totalBookings: number;
  utilizationRate: number;
  revenue: number;
}

export interface FacilityUtilizationReport {
  facilities: FacilityUsage[];
}

export interface ExecutiveSummary {
  totalIncome: number;
  totalExpense: number;
  netSurplus: number;
  collectionRate: number;
  defaulterCount: number;
  openTickets: number;
  breachedTickets: number;
  avgFacilityUtilization: number;
}

export const reports = {
  financial: (sid: string) =>
    get<FinancialReport>(`/api/societies/${sid}/reports/financial`),
  collection: (sid: string) =>
    get<CollectionReport>(`/api/societies/${sid}/reports/collection`),
  slaCompliance: (sid: string) =>
    get<SlaComplianceReport>(`/api/societies/${sid}/reports/sla-compliance`),
  facilityUtilization: (sid: string) =>
    get<FacilityUtilizationReport>(
      `/api/societies/${sid}/reports/facility-utilization`,
    ),
  executiveSummary: (sid: string) =>
    get<ExecutiveSummary>(`/api/societies/${sid}/reports/executive-summary`),
  exportBlob: async (
    sid: string,
    type: "financial" | "collection" | "sla" | "facility",
    format: "pdf" | "csv",
  ): Promise<Blob> => {
    const token = tokenStore.getAccess();
    const res = await fetch(
      `/api/societies/${sid}/reports/export?type=${type}&format=${format}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );
    if (!res.ok) throw new Error("Failed to export report");
    return res.blob();
  },
};

export interface ReportSchedule {
  id: string;
  societyId: string;
  reportType: "financial" | "collection" | "sla" | "facility";
  frequency: "DAILY" | "WEEKLY" | "MONTHLY";
  recipientEmails: string;
  active: boolean;
  lastSentAt?: string;
}

export const reportSchedules = {
  list: (sid: string) =>
    getList<ReportSchedule>(`/api/societies/${sid}/report-schedules`),
  create: (sid: string, data: Partial<ReportSchedule>) =>
    post<ReportSchedule>(`/api/societies/${sid}/report-schedules`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/report-schedules/${id}`),
};

/* ─── Monthly Maintenance Charges ───────────────────────────────────────── */

export interface MaintenanceCharge {
  id: string;
  societyId: string;
  name: string;
  amount: number;
  applicableTo: "all" | "owner" | "tenant" | "family";
  blockId?: string | null;
  blockName?: string;
  isActive: boolean;
  createdAt: string;
}

export interface MaintenanceChargeConfig {
  societyId: string;
  dueDayOfMonth: number;
  gracePeriodDays: number;
  lateFeePct: number;
  isActive: boolean;
  gstRatePct: number;
  lateFeeMode: "FLAT_PCT" | "COMPOUND_PCT";
  autoGenerateInvoices: boolean;
  charges: MaintenanceCharge[];
}

export const maintenanceCharges = {
  getConfig: (sid: string) =>
    get<MaintenanceChargeConfig>(
      `/api/societies/${sid}/maintenance-charges/config`,
    ),
  saveConfig: (sid: string, data: Partial<MaintenanceChargeConfig>) =>
    put<MaintenanceChargeConfig>(
      `/api/societies/${sid}/maintenance-charges/config`,
      data,
    ),
  list: (sid: string) =>
    getList<MaintenanceCharge>(`/api/societies/${sid}/maintenance-charges`),
  create: (sid: string, data: Partial<MaintenanceCharge>) =>
    post<MaintenanceCharge>(`/api/societies/${sid}/maintenance-charges`, data),
  update: (sid: string, id: string, data: Partial<MaintenanceCharge>) =>
    put<MaintenanceCharge>(
      `/api/societies/${sid}/maintenance-charges/${id}`,
      data,
    ),
  remove: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/maintenance-charges/${id}`),
  generateInvoices: (sid: string, month: string) =>
    post<{ generated: number }>(
      `/api/societies/${sid}/maintenance-charges/generate`,
      { month },
    ),
  applyLateFees: (sid: string) =>
    post<{ updated: number }>(
      `/api/societies/${sid}/maintenance-charges/apply-late-fees`,
    ),
};

/* ─── Payment Config ────────────────────────────────────────────────────── */

export interface PaymentConfig {
  societyId: string;
  accountName?: string;
  accountNumber?: string;
  ifscCode?: string;
  bankName?: string;
  branchName?: string;
  upiId?: string;
  paymentInstructions?: string;
  acceptBankTransfer: boolean;
  acceptUpi: boolean;
  acceptCash: boolean;
  acceptCheque: boolean;
}

export const paymentConfig = {
  get: (sid: string) =>
    get<PaymentConfig>(`/api/societies/${sid}/payment-config`),
  save: (sid: string, data: Partial<PaymentConfig>) =>
    put<PaymentConfig>(`/api/societies/${sid}/payment-config`, data),
};

/* ─── Resident Billing Summary ──────────────────────────────────────────── */

export interface ResidentBillingSummary {
  currentMonthDue: number;
  totalOutstanding: number;
  currentMonthStatus: "paid" | "unpaid" | "overdue" | "partial";
  lastPaidAt?: string;
  charges: { name: string; amount: number }[];
  invoices: Invoice[];
}

export const residentBilling = {
  mySummary: (sid: string) =>
    get<ResidentBillingSummary>(`/api/societies/${sid}/billing/my-summary`),
};

/* ─── Chat (private messages) ───────────────────────────────────────────── */

export type ChatParticipantType = "resident" | "admin";

export interface PrivateMessage {
  id: string;
  societyId: string;
  senderType: ChatParticipantType;
  senderId: string;
  receiverType: ChatParticipantType;
  receiverId: string;
  body: string;
  readAt?: string;
  createdAt: string;
}

export interface ChatContact {
  id: string;
  fullName: string;
}

export interface ConversationSummary {
  aType: ChatParticipantType;
  aId: string;
  aName: string;
  bType: ChatParticipantType;
  bId: string;
  bName: string;
  lastMessage: string;
  lastMessageAt: string;
  messageCount: number;
}

export const chat = {
  send: (
    sid: string,
    receiverType: ChatParticipantType,
    receiverId: string,
    body: string,
  ) =>
    post<PrivateMessage>(`/api/societies/${sid}/messages`, {
      receiverType,
      receiverId,
      body,
    }),
  thread: (sid: string, otherType: ChatParticipantType, otherId: string) =>
    getList<PrivateMessage>(
      `/api/societies/${sid}/messages/with/${otherType}/${otherId}`,
    ),
  markRead: (sid: string, id: string) =>
    patch<PrivateMessage>(`/api/societies/${sid}/messages/${id}/read`),
  /** Society admins a resident can start a conversation with. */
  adminContacts: (sid: string) =>
    getList<ChatContact>(`/api/societies/${sid}/messages/admins`),
  /** [Admin] Every conversation in the society, for moderation/oversight. */
  adminConversations: (sid: string) =>
    getList<ConversationSummary>(
      `/api/societies/${sid}/messages/admin/conversations`,
    ),
  /** [Admin] View any conversation between two participants. */
  adminThread: (
    sid: string,
    aType: ChatParticipantType,
    aId: string,
    bType: ChatParticipantType,
    bId: string,
  ) =>
    getList<PrivateMessage>(
      `/api/societies/${sid}/messages/admin/thread/${aType}/${aId}/${bType}/${bId}`,
    ),
};

/* ─── Committee meetings ────────────────────────────────────────────────── */

export interface CommitteeMeeting {
  id: string;
  societyId: string;
  title: string;
  description?: string;
  scheduledAt: string;
  durationMinutes: number;
  joinUrl: string;
  organizedBy?: string;
  status: "SCHEDULED" | "COMPLETED" | "CANCELLED";
}

export const committeeMeetings = {
  list: (sid: string) =>
    getList<CommitteeMeeting>(`/api/societies/${sid}/committee-meetings`),
  create: (sid: string, data: Partial<CommitteeMeeting>) =>
    post<CommitteeMeeting>(`/api/societies/${sid}/committee-meetings`, data),
  cancel: (sid: string, id: string) =>
    patch<CommitteeMeeting>(
      `/api/societies/${sid}/committee-meetings/${id}/cancel`,
    ),
};

/* ─── Marketplace ────────────────────────────────────────────────────────── */

export interface MarketplaceListing {
  id: string;
  societyId: string;
  sellerResidentId: string;
  title: string;
  description?: string;
  category?: string;
  price: number;
  photoBase64?: string;
  status: "ACTIVE" | "SOLD" | "REMOVED";
}

export interface MarketplaceOrder {
  id: string;
  listingId: string;
  buyerResidentId: string;
  amount: number;
  status:
    | "PENDING"
    | "ESCROWED"
    | "SHIPPED"
    | "COMPLETED"
    | "CANCELLED"
    | "REFUNDED";
  razorpayOrderId?: string;
  escrowedAt?: string;
  releasedAt?: string;
}

export const marketplace = {
  listActive: (sid: string) =>
    getList<MarketplaceListing>(`/api/societies/${sid}/marketplace/listings`),
  listActivePage: (
    sid: string,
    params: { page?: number; size?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    qs.set("page", String(params.page ?? 0));
    qs.set("size", String(params.size ?? 20));
    return getPage<MarketplaceListing>(
      `/api/societies/${sid}/marketplace/listings?${qs.toString()}`,
    );
  },
  createListing: (sid: string, data: Partial<MarketplaceListing>) =>
    post<MarketplaceListing>(
      `/api/societies/${sid}/marketplace/listings`,
      data,
    ),
  purchase: (sid: string, listingId: string) =>
    post<MarketplaceOrder>(
      `/api/societies/${sid}/marketplace/listings/${listingId}/orders`,
    ),
  createPaymentOrder: (sid: string, listingId: string, orderId: string) =>
    post<PaymentOrder>(
      `/api/societies/${sid}/marketplace/listings/${listingId}/orders/${orderId}/payment-order`,
    ),
  verifyPayment: (
    sid: string,
    listingId: string,
    orderId: string,
    data: { razorpayPaymentId: string; razorpaySignature: string },
  ) =>
    post<MarketplaceOrder>(
      `/api/societies/${sid}/marketplace/listings/${listingId}/orders/${orderId}/payment-verify`,
      data,
    ),
  markShipped: (sid: string, listingId: string, orderId: string) =>
    patch<MarketplaceOrder>(
      `/api/societies/${sid}/marketplace/listings/${listingId}/orders/${orderId}/ship`,
    ),
  confirmReceived: (sid: string, listingId: string, orderId: string) =>
    patch<MarketplaceOrder>(
      `/api/societies/${sid}/marketplace/listings/${listingId}/orders/${orderId}/confirm-received`,
    ),
  removeListing: (sid: string, listingId: string) =>
    del<void>(`/api/societies/${sid}/marketplace/listings/${listingId}`),
};

/* ─── Violations ─────────────────────────────────────────────────────────── */

export interface Violation {
  id: string;
  societyId: string;
  unitId?: string;
  residentId?: string;
  category: string;
  description?: string;
  status: "OPEN" | "RESOLVED" | "DISMISSED";
  reportedBy?: string;
  resolvedBy?: string;
  resolvedAt?: string;
  resolutionNotes?: string;
}

export const violations = {
  list: (sid: string) => getList<Violation>(`/api/societies/${sid}/violations`),
  create: (sid: string, data: Partial<Violation>) =>
    post<Violation>(`/api/societies/${sid}/violations`, data),
  resolve: (
    sid: string,
    id: string,
    status: string,
    resolutionNotes?: string,
  ) =>
    patch<Violation>(`/api/societies/${sid}/violations/${id}/resolve`, {
      status,
      resolutionNotes,
    }),
  update: (sid: string, id: string, data: Partial<Violation>) =>
    patch<Violation>(`/api/societies/${sid}/violations/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/violations/${id}`),
};

/* ─── Vehicles ───────────────────────────────────────────────────────────── */

export interface Vehicle {
  id: string;
  societyId: string;
  residentId: string;
  unitId?: string;
  vehicleType: "CAR" | "BIKE" | "OTHER";
  registrationNumber: string;
  makeModel?: string;
  color?: string;
  parkingSlotId?: string;
  active: boolean;
}

export interface VehicleLookupResult {
  id: string;
  registrationNumber: string;
  vehicleType: string;
  makeModel?: string;
  color?: string;
  residentId?: string;
  residentName?: string;
  residentPhone?: string;
  unitId?: string;
  unitNumber?: string;
  blockName?: string;
}

export const vehicles = {
  list: (sid: string) => getList<Vehicle>(`/api/societies/${sid}/vehicles`),
  create: (sid: string, data: Partial<Vehicle>) =>
    post<Vehicle>(`/api/societies/${sid}/vehicles`, data),
  update: (sid: string, id: string, data: Partial<Vehicle>) =>
    patch<Vehicle>(`/api/societies/${sid}/vehicles/${id}`, data),
  delete: (sid: string, id: string) =>
    del<void>(`/api/societies/${sid}/vehicles/${id}`),
  lookup: (sid: string, registrationNumber: string) =>
    get<VehicleLookupResult | null>(
      `/api/societies/${sid}/vehicles/lookup?registrationNumber=${encodeURIComponent(registrationNumber)}`,
    ),
  importFile: (sid: string, file: File) =>
    uploadFile<CsvImportResult>(`/api/societies/${sid}/vehicles/import`, file),
  exportCsv: (sid: string) =>
    downloadFile(`/api/societies/${sid}/vehicles/export`),
};

/* ─── Deposit tracking ───────────────────────────────────────────────────── */

export interface DepositTransaction {
  id: string;
  unitId: string;
  type: string;
  amount: number;
  reason?: string;
  transactionDate: string;
}

export interface DepositBalance {
  unitId: string;
  balance: number;
  transactions: DepositTransaction[];
}

export interface DepositUnitSummary {
  unitId: string;
  unitNumber: string;
  blockName?: string;
  residentName?: string;
  residentPhone?: string;
  balance: number;
}

export const deposits = {
  getSummary: (sid: string) =>
    getList<DepositUnitSummary>(`/api/societies/${sid}/deposits/summary`),
  getBalance: (sid: string, unitId: string) =>
    get<DepositBalance>(`/api/societies/${sid}/deposits/units/${unitId}`),
  record: (
    sid: string,
    data: {
      unitId: string;
      residentId?: string;
      type: string;
      amount: number;
      reason?: string;
      transactionDate: string;
    },
  ) => post<DepositTransaction>(`/api/societies/${sid}/deposits`, data),
};

/* ─── Bulk resident CSV import ──────────────────────────────────────────── */

export interface CsvImportResult {
  created: number;
  failed: number;
  errors: string[];
}

export const residentImport = {
  importCsv: (sid: string, file: File) =>
    uploadFile<CsvImportResult>(`/api/societies/${sid}/residents/import`, file),
};

/* ─── Facility waitlist + guest pass ────────────────────────────────────── */

export interface WaitlistEntry {
  id: string;
  facilityId: string;
  residentId: string;
  requestedDate: string;
  slotStart: string;
  slotEnd: string;
  status: "WAITING" | "NOTIFIED" | "EXPIRED" | "FULFILLED";
}

export interface GuestPass {
  id: string;
  bookingId: string;
  visitorId: string;
  guestName: string;
  guestPhone?: string;
  qrToken: string;
  otpCode: string;
}

export const facilityWaitlist = {
  list: (sid: string, fid: string) =>
    getList<WaitlistEntry>(`/api/societies/${sid}/facilities/${fid}/waitlist`),
  join: (
    sid: string,
    fid: string,
    data: { requestedDate: string; slotStart: string; slotEnd: string },
  ) =>
    post<WaitlistEntry>(
      `/api/societies/${sid}/facilities/${fid}/waitlist`,
      data,
    ),
  createGuestPass: (
    sid: string,
    fid: string,
    bid: string,
    data: { guestName: string; guestPhone?: string },
  ) =>
    post<GuestPass>(
      `/api/societies/${sid}/facilities/${fid}/bookings/${bid}/guest-pass`,
      data,
    ),
};

/* ─── Facility utilisation heatmap ──────────────────────────────────────── */

export interface HeatmapCell {
  dayOfWeek: string;
  hourOfDay: number;
  bookingCount: number;
}

export const facilityHeatmap = {
  get: (sid: string) =>
    get<{ cells: HeatmapCell[] }>(
      `/api/societies/${sid}/reports/facility-utilization/heatmap`,
    ),
};

/* ─── Notifications ─────────────────────────────────────────────────────── */

export interface AppNotification {
  id: string;
  recipientUserId: string;
  societyId?: string;
  type: string;
  title: string;
  body?: string;
  read: boolean;
  createdAt: string;
}

export type NexoraRole =
  | "superadmin"
  | "admin"
  | "staff"
  | "guard"
  | "treasurer"
  | "view-only-admin"
  | "theme-manager"
  | "analyst"
  | "viewer"
  | "resident"
  | "tenant";

export const alertSubscriptions = {
  get: (sid: string, type: "SOS" | "CHILD_SAFETY") =>
    getList<NexoraRole>(
      `/api/societies/${sid}/alert-subscriptions?type=${type}`,
    ),
  set: (sid: string, type: "SOS" | "CHILD_SAFETY", roles: NexoraRole[]) =>
    put<void>(`/api/societies/${sid}/alert-subscriptions?type=${type}`, {
      roles,
    }),
};

export const notifications = {
  list: (page = 0, size = 20) =>
    getPage<AppNotification>(
      `/api/users/me/notifications?page=${page}&size=${size}&sort=createdAt,desc`,
    ),
  unreadCount: () =>
    get<{ count: number }>("/api/users/me/notifications/unread-count"),
  markRead: (id: string) =>
    patch<void>(`/api/users/me/notifications/${id}/read`),
  registerDeviceToken: (pushToken: string) =>
    post<void>("/api/users/me/device-tokens", { pushToken, platform: "web" }),
  unregisterDeviceToken: (pushToken: string) =>
    del<void>(
      `/api/users/me/device-tokens?pushToken=${encodeURIComponent(pushToken)}`,
    ),
};

export interface CloudUsageMetric {
  used: number;
  cap: number;
}

export interface CloudRunServiceInfo {
  name: string;
  region: string;
  minInstances: number;
  maxInstances: number;
  cpu: string;
  memory: string;
  currentInstanceCount: number;
  deploymentCount: number;
}

export interface CloudProjectUsage {
  projectId: string;
  requests: number;
  vCpuSeconds: number;
  memoryGibSeconds: number;
  networkEgressBytes: number;
  buildCount: number;
  buildMinutes: number;
  deploymentCount: number;
  previousRequests: number;
  previousVCpuSeconds: number;
  previousMemoryGibSeconds: number;
  previousNetworkEgressBytes: number;
  previousBuildCount: number;
  previousBuildMinutes: number;
  services: CloudRunServiceInfo[];
}

export interface CloudPreviousMonthSummary {
  requests: number;
  vCpuSeconds: number;
  memoryGibSeconds: number;
  networkEgressBytes: number;
  buildCount: number;
  buildMinutes: number;
}

export interface CloudUsageReport {
  requests: CloudUsageMetric;
  vCpuSeconds: CloudUsageMetric;
  memoryGibSeconds: CloudUsageMetric;
  networkEgressBytes: CloudUsageMetric;
  totalBuildCount: number;
  totalBuildMinutes: number;
  totalDeploymentCount: number;
  previousMonth: CloudPreviousMonthSummary;
  projects: CloudProjectUsage[];
}

export const cloudUsage = {
  get: () => get<CloudUsageReport>("/api/v1/admin/cloud-usage"),
  exportExcel: async (retry = true): Promise<Blob> => {
    const token = tokenStore.getAccess();
    const res = await fetch("/api/v1/admin/cloud-usage/export", {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.status === 401 && retry) {
      const refreshed = await ensureFreshToken();
      if (refreshed) return cloudUsage.exportExcel(false);
    }
    if (!res.ok) throw new Error("Failed to export cloud usage report");
    return res.blob();
  },
};
